import SEO from '@/components/SEO';
const Privacy = () => {
  return <>
      <SEO title="Privacy Policy - SeniorThrive" description="SeniorThrive's comprehensive privacy and data sharing policy, including HIPAA compliance and ThriveCircle data-sharing guidelines." />
      
      <div className="min-h-screen bg-gradient-subtle">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold text-foreground mb-4">
                SeniorThrive Privacy & Data Sharing Policy
              </h1>
              <p className="text-lg text-muted-foreground">
                Last Updated and Effective Date: May 20, 2025
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                This policy replaces all previous versions.
              </p>
            </div>

            {/* Content */}
            <div className="prose prose-lg max-w-none">
              <div className="bg-card border border-border rounded-xl p-8 mb-8 shadow-card">
                <h2 className="text-2xl font-bold text-foreground mb-6">Your Privacy at a Glance (Key Takeaways)</h2>
                
                <div className="space-y-4 text-muted-foreground">
                  <p>We know privacy policies can be long, so here's a quick overview:</p>
                  <ul className="space-y-2 list-disc list-inside">
                    <li><strong>We Collect Your Info:</strong> To provide and improve SeniorThrive, and to keep it secure.</li>
                    <li><strong>Your Health Info is Special (PHI):</strong> It gets extra protection and is only shared with your explicit consent through ThriveCircle.</li>
                    <li><strong>You're in Control:</strong> You can see, change, or ask us to delete your information. You also control marketing communications.</li>
                    <li><strong>Strong Security:</strong> We use robust measures to protect your data.</li>
                    <li><strong>ThriveCircle Sharing:</strong> You decide what PHI is shared with whom in your care circle.</li>
                    <li><strong>Our Full Terms:</strong> This policy works together with our Terms of Service, which has more legal details.</li>
                  </ul>
                  <p><strong>Questions?</strong> We're here to help. Contact us at <a href="mailto:privacy@seniorthrive.com" className="text-primary hover:underline">privacy@seniorthrive.com</a>.</p>
                </div>
              </div>

              <div className="space-y-12">
                <section>
                  <h2 className="text-2xl font-bold text-foreground mb-4">Respecting Your Privacy: Secure & Trustworthy</h2>
                  <p className="text-muted-foreground">
                    In the digital age, privacy is vital—especially when it involves your home and health. At SeniorThrive, we place your privacy and security at the core of our service. We're not just creating a platform; we're building a trusted environment where we are deeply committed to protecting your safety and confidentiality.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-foreground mb-4">Our Commitment to Health Data Privacy (HIPAA)</h2>
                  <p className="text-muted-foreground">
                    SeniorThrive is committed to protecting the privacy and security of your Protected Health Information (PHI) as defined by the Health Insurance Portability and Accountability Act of 1996 (HIPAA) and applicable state laws. When we handle PHI on behalf of healthcare providers or as part of our services, we act as a "Business Associate" under HIPAA and adhere to the required safeguards and agreements.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-foreground mb-4">Scope & Acceptance</h2>
                  <p className="text-muted-foreground mb-4">
                    By creating an account or using the SeniorThrive web app or any ThriveCircle feature ("Services"), you confirm that you have read, understood, and agree to this entire Privacy & Data Sharing Policy and our Terms of Service (which are incorporated herein by reference and govern your use of our Services). You are also responsible for taking reasonable steps to protect your account, such as using a strong password and keeping your login credentials confidential.
                  </p>
                  <p className="text-muted-foreground">
                    <strong>Who's Covered:</strong> All users—including Focus Older Adults (OAs), Circle Administrators, Professional Caregivers, and Family Members—whenever you access any SeniorThrive feature.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-foreground mb-4">Information We Collect</h2>
                  <p className="text-muted-foreground mb-4">We collect and process the following categories of information:</p>
                  
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Account & Contact:</h3>
                      <p className="text-muted-foreground">Name, email, phone number, password.</p>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Device & Usage:</h3>
                      <p className="text-muted-foreground">Device type, operating system, IP address, browser type, features used, errors encountered.</p>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Location (with your consent):</h3>
                      <p className="text-muted-foreground">Precise or coarse GPS data to enable location-based services.</p>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Profile & User-Generated:</h3>
                      <p className="text-muted-foreground">Photos you upload, free-text entries, support requests.</p>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Health & Fitness (if you opt in):</h3>
                      <ul className="list-disc list-inside text-muted-foreground space-y-1">
                        <li><strong>PHI:</strong> Activities of Daily Living (ADL), Instrumental ADL, BMI, medication logs, fall incidents, wellness check-ins, mood/symptom tracking, vital signs.</li>
                        <li><strong>Non-PHI:</strong> Aggregate metrics (ThriveScore), general activity counts.</li>
                      </ul>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Household & Environmental:</h3>
                      <p className="text-muted-foreground">Room scan photos, pet information, household to-do lists.</p>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Financial (if you transact):</h3>
                      <p className="text-muted-foreground">Payment details collected via secure third-party processors (we never store full card numbers).</p>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Cookies & Tracking:</h3>
                      <ul className="list-disc list-inside text-muted-foreground space-y-1">
                        <li>We use cookies, beacons, and similar technologies for analytics and personalization.</li>
                        <li><strong>Marketing Cookies (Optional):</strong> If you opt in, we and selected third parties may use these to measure ad performance and serve relevant ads.</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="mt-6">
                    <h3 className="text-lg font-semibold text-foreground">De-Identification:</h3>
                    <p className="text-muted-foreground">
                      When processing data for analytics or research, we strip direct identifiers (name, email, device ID) and employ methods consistent with recognized de-identification standards (such as those outlined under HIPAA) to prevent re-identification. We avoid combining this de-identified data with other datasets that could lead to re-identification unless you explicitly opt in.
                    </p>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-foreground mb-4">Tracking & Third-Party Analytics</h2>
                  <p className="text-muted-foreground mb-4">
                    We and selected third-party partners may link your SeniorThrive web-app usage with activity on other websites for analytics, advertising measurement, or retargeting.
                  </p>
                  
                  <div className="space-y-3">
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Cookie Banner Controls:</h3>
                      <p className="text-muted-foreground">Our cookie banner offers at least three tiers—Necessary, Functional, and Marketing. You choose which to allow.</p>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Browser Settings:</h3>
                      <p className="text-muted-foreground">You can disable or block third-party cookies in your browser (Chrome, Safari, Firefox, etc.).</p>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Do-Not-Track:</h3>
                      <p className="text-muted-foreground">While browsers may send a "Do Not Track" signal, enforcement varies. Use your cookie banner choices and browser settings for reliable control.</p>
                    </div>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-foreground mb-4">How We Use & Share Your Information</h2>
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">App Functionality & Security:</h3>
                      <p className="text-muted-foreground">To provide, maintain, and secure our Services, including authentication, fraud prevention, uptime monitoring, customer support.</p>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Personalization & Analytics:</h3>
                      <p className="text-muted-foreground">Content recommendations, troubleshooting, feature testing, service improvement.</p>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Care Collaboration (ThriveCircle):</h3>
                      <p className="text-muted-foreground">With your explicit approval, share precise data categories with caregivers and family as detailed in the ThriveCircle Addendum.</p>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Communication:</h3>
                      <p className="text-muted-foreground">Notifications, onboarding emails, product updates, service announcements (you control marketing opt-in/out).</p>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Third-Party Services & Advertising:</h3>
                      <p className="text-muted-foreground">We share information with third-party service providers who assist us in operating our Services, only under strict confidentiality agreements (including Business Associate Agreements where PHI is involved). Advertising data is shared only with your opt-in.</p>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Legal Compliance:</h3>
                      <p className="text-muted-foreground">Responding to lawful requests from public and government authorities, complying with legal processes, enforcing our terms and conditions, protecting our operations or those of any of our affiliates, protecting our rights, privacy, safety or property, and/or that of our affiliates, you or others; and allowing us to pursue available remedies or limit the damages that we may sustain.</p>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Business Transfers:</h3>
                      <p className="text-muted-foreground">In a merger, acquisition, financing, reorganization, bankruptcy, or sale of all or a portion of our assets, your information may be sold or transferred as part of that transaction. Your rights and protections regarding your PHI will travel with it.</p>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Referral Introductions:</h3>
                      <p className="text-muted-foreground">If you request a referral through SeniorThrive, we may collect details you provide (such as your name, contact information, and general description of your home safety or accessibility needs) to help identify an appropriate service provider. We may share only the necessary details with potential providers to facilitate an introduction. We do not sell or rent this information, and we encourage you to review the privacy practices of any provider you choose to engage.</p>
                    </div>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-foreground mb-4">Integration with Third-Party Services</h2>
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">YouTube API Services:</h3>
                      <p className="text-muted-foreground">Enables video features (e.g., embedded tutorials, SeniorThrive Explores series).</p>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Other Integrations:</h3>
                      <p className="text-muted-foreground">Fitness trackers, voice assistants, analytics platforms—all vetted for appropriate privacy and security standards, including data processing agreements or Business Associate Agreements where necessary.</p>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Terms of Use with YouTube:</h3>
                      <p className="text-muted-foreground">By using SeniorThrive features that interact with YouTube, you agree to YouTube's Terms of Service.</p>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Third-Party Privacy Policies:</h3>
                      <p className="text-muted-foreground">For video functionality, we comply with Google's Privacy Policy. Please review it to understand how Google handles data in these contexts.</p>
                    </div>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-foreground mb-4">Your Rights & Choices</h2>
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Access & Correction:</h3>
                      <p className="text-muted-foreground">View or edit your account details and PHI entries at any time through your account settings.</p>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Export & Deletion:</h3>
                      <p className="text-muted-foreground">Download your data (e.g., in CSV/JSON format) or request permanent deletion of your account and associated data, subject to legal retention requirements and our data retention policies.</p>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Consent Management:</h3>
                      <p className="text-muted-foreground">Toggle data-sharing settings in Settings → Privacy, especially for ThriveCircle features.</p>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Cookie Controls:</h3>
                      <p className="text-muted-foreground">Adjust cookie preferences via our cookie banner or your browser settings.</p>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Email Opt-Out:</h3>
                      <p className="text-muted-foreground">Every marketing email footer has an "Unsubscribe" link; you can also adjust preferences under Settings → Notifications.</p>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Children's Privacy:</h3>
                      <p className="text-muted-foreground">We do not knowingly collect personal information from children under 13 (or a higher age threshold where applicable by law). Our services are not directed to children. If we learn we have collected a child's personal information, we will promptly delete it.</p>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">State-Specific Privacy Rights:</h3>
                      <p className="text-muted-foreground">Depending on your state of residence (e.g., California), you may have additional privacy rights regarding your personal information. Please contact us if you have questions about rights that may apply to you.</p>
                    </div>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-foreground mb-4">Email Communication & Consent</h2>
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Opt-In Required:</h3>
                      <p className="text-muted-foreground">During signup, you explicitly consent to receive emails—each opt-in is logged with a timestamp.</p>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Confirmation Email:</h3>
                      <p className="text-muted-foreground">After you opt in, we send a verification link before any regular communications begin.</p>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Types of Messages:</h3>
                      <p className="text-muted-foreground">Welcome/onboarding, wellness reminders, product updates, community news, legal/security notices.</p>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Withdraw Consent:</h3>
                      <p className="text-muted-foreground">Use the "Unsubscribe" link in any email or adjust in Settings → Notifications.</p>
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">Third-Party Delivery:</h3>
                      <p className="text-muted-foreground">We use trusted providers (e.g., ActiveCampaign, OneSignal) under strict confidentiality; we never sell your email address.</p>
                    </div>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-foreground mb-4">Security Measures</h2>
                  <p className="text-muted-foreground mb-4">
                    We employ robust technical, administrative, and physical safeguards designed to protect your information from unauthorized access, use, alteration, and disclosure. These include:
                  </p>
                  
                  <ul className="list-disc list-inside text-muted-foreground space-y-2">
                    <li><strong>Encryption:</strong> AES-256 at rest; TLS 1.2+ in transit.</li>
                    <li><strong>Access Controls:</strong> Role-based permission checks on every PHI request.</li>
                    <li><strong>Audit & Breach Monitoring:</strong> Immutable logs of all PHI actions; automated alerts for suspicious activity.</li>
                    <li><strong>Business Associate Agreements (BAAs):</strong> We enter into BAAs with any third party handling PHI on our behalf, as required by HIPAA.</li>
                    <li><strong>Regular Security Assessments:</strong> We conduct periodic security reviews and vulnerability assessments.</li>
                  </ul>
                  
                  <p className="text-muted-foreground mt-4">
                    While we take significant measures to protect your data, no security system is impenetrable. We cannot guarantee the absolute security of your information. In the event of a data breach involving your personal information where notification is required by law, we will notify you in accordance with applicable legal requirements.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-foreground mb-4">Changes to This Policy & Contact</h2>
                  <p className="text-muted-foreground mb-4">
                    We'll post updates to this Privacy Policy here and notify you (e.g., via email or in-app notification) at least 30 days before material changes take effect. Your continued use of SeniorThrive after such notice constitutes your acceptance of the revised policy.
                  </p>
                  
                  <div className="space-y-2">
                    <p className="text-muted-foreground">
                      <strong>Questions or Complaints:</strong> <a href="mailto:privacy@seniorthrive.com" className="text-primary hover:underline">privacy@seniorthrive.com</a> | <a href="tel:415-555-1234" className="text-primary hover:underline">(415) 555-1234</a>
                    </p>
                    <p className="text-muted-foreground">
                      <strong>Accessibility:</strong> We are committed to making this policy accessible. If you need it in an alternative format, please contact us.
                    </p>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-foreground mb-4">Acknowledgement</h2>
                  <p className="text-muted-foreground">
                    By continuing to use SeniorThrive—via the web app or ThriveCircle features—you confirm you've read, understood, and accepted this Privacy & Data Sharing Policy and our Terms of Service in their entirety.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-foreground mb-4">Governing Law & Our Terms of Service</h2>
                  <p className="text-muted-foreground mb-4">
                    This Privacy Policy and any disputes related to it or your use of SeniorThrive Services will be governed by and construed in accordance with the laws of the State of [Insert Your State, e.g., California], without regard to its conflict of law principles.
                  </p>
                  <p className="text-muted-foreground">
                    Our Terms of Service contain important provisions regarding dispute resolution (which may include arbitration and a class action waiver), limitations on liability, and other terms that govern your use of our Services. Please review them carefully.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-foreground mb-4">International Data Transfers</h2>
                  <p className="text-muted-foreground">
                    SeniorThrive is based in the United States. If you are accessing our Services from outside the United States, please be aware that your information, including personal data and PHI, may be transferred to, stored, and processed in the United States, where our servers are located and our central database is operated. The data protection and other laws of the United States might not be as comprehensive as those in your country. By using our Services, you consent to your information being transferred to our facilities and to the facilities of those third parties with whom we share it as described in this Privacy Policy.
                  </p>
                </section>

                <section className="bg-card border border-border rounded-xl p-8 shadow-card">
                  <h2 className="text-2xl font-bold text-foreground mb-6">ThriveCircle Data-Sharing Addendum</h2>
                  <p className="text-muted-foreground mb-6">
                    This Addendum applies only when you use any ThriveCircle features centered on a Focus Older Adult's (OA's) care. It details how data, especially Protected Health Information (PHI), is shared within a ThriveCircle.
                  </p>
                  
                  <div className="space-y-8">
                    <div>
                      <h3 className="text-xl font-bold text-foreground mb-4">Key Definitions</h3>
                      <div className="space-y-4">
                        <div>
                          <h4 className="text-lg font-semibold text-foreground">PHI (Protected Health Information):</h4>
                          <p className="text-muted-foreground">Information that relates to your past, present, or future physical or mental health or condition, the provision of health care to you, or the past, present, or future payment for the provision of health care to you, and that identifies you or for which there is a reasonable basis to believe it can be used to identify you. Examples include medication logs, fall incidents, vital signs, and mood/symptom entries.</p>
                        </div>
                        
                        <div>
                          <h4 className="text-lg font-semibold text-foreground">Non-PHI:</h4>
                          <p className="text-muted-foreground">Other data that is not PHI. Examples include room scans (unless they incidentally contain PHI), general appointments, and to-do lists.</p>
                        </div>
                        
                        <div>
                          <h4 className="text-lg font-semibold text-foreground">Roles:</h4>
                          <ul className="list-disc list-inside text-muted-foreground space-y-1">
                            <li><strong>Focus OA:</strong> The older adult whose data lies at the heart of the Circle.</li>
                            <li><strong>Circle Admin:</strong> Manages membership & shared household information.</li>
                            <li><strong>Professional Caregiver:</strong> Enters and views clinical PHI necessary for care, often operating under a BAA with SeniorThrive or their employing agency.</li>
                            <li><strong>Family Member:</strong> Views approved data categories, adds non-PHI entries, and may sync calendars.</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl font-bold text-foreground mb-4">Role-Based Permissions & PHI</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse border border-border">
                          <thead>
                            <tr className="bg-accent">
                              <th className="border border-border p-3 text-left font-semibold">Action / Category</th>
                              <th className="border border-border p-3 text-left font-semibold">Focus OA</th>
                              <th className="border border-border p-3 text-left font-semibold">Circle Admin</th>
                              <th className="border border-border p-3 text-left font-semibold">Professional Caregiver</th>
                              <th className="border border-border p-3 text-left font-semibold">Family Member</th>
                              <th className="border border-border p-3 text-left font-semibold">PHI?</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td className="border border-border p-3">Medications</td>
                              <td className="border border-border p-3 text-green-600">✓</td>
                              <td className="border border-border p-3 text-yellow-600">With Consent</td>
                              <td className="border border-border p-3 text-yellow-600">With Consent</td>
                              <td className="border border-border p-3 text-yellow-600">With Consent</td>
                              <td className="border border-border p-3 font-semibold">PHI</td>
                            </tr>
                            <tr>
                              <td className="border border-border p-3">Fall Incident Reports</td>
                              <td className="border border-border p-3 text-green-600">✓</td>
                              <td className="border border-border p-3 text-yellow-600">With Consent</td>
                              <td className="border border-border p-3 text-yellow-600">With Consent</td>
                              <td className="border border-border p-3 text-yellow-600">With Consent</td>
                              <td className="border border-border p-3 font-semibold">PHI</td>
                            </tr>
                            <tr>
                              <td className="border border-border p-3">Wellness Check-Ins & Mood Logs</td>
                              <td className="border border-border p-3 text-green-600">✓</td>
                              <td className="border border-border p-3 text-yellow-600">With Consent</td>
                              <td className="border border-border p-3 text-yellow-600">With Consent</td>
                              <td className="border border-border p-3 text-yellow-600">With Consent</td>
                              <td className="border border-border p-3 font-semibold">PHI</td>
                            </tr>
                            <tr>
                              <td className="border border-border p-3">Vital Signs & Clinical Notes</td>
                              <td className="border border-border p-3 text-green-600">✓</td>
                              <td className="border border-border p-3 text-yellow-600">With Consent</td>
                              <td className="border border-border p-3 text-yellow-600">With Consent</td>
                              <td className="border border-border p-3 text-yellow-600">With Consent</td>
                              <td className="border border-border p-3 font-semibold">PHI</td>
                            </tr>
                            <tr>
                              <td className="border border-border p-3">Medical Appointments</td>
                              <td className="border border-border p-3 text-green-600">✓</td>
                              <td className="border border-border p-3 text-yellow-600">With Consent</td>
                              <td className="border border-border p-3 text-yellow-600">With Consent</td>
                              <td className="border border-border p-3 text-yellow-600">With Consent</td>
                              <td className="border border-border p-3 font-semibold">PHI</td>
                            </tr>
                            <tr>
                              <td className="border border-border p-3">General Appointments</td>
                              <td className="border border-border p-3 text-green-600">✓</td>
                              <td className="border border-border p-3 text-green-600">✓</td>
                              <td className="border border-border p-3 text-green-600">✓</td>
                              <td className="border border-border p-3 text-green-600">✓</td>
                              <td className="border border-border p-3">Non-PHI</td>
                            </tr>
                            <tr>
                              <td className="border border-border p-3">Household / Room Scans</td>
                              <td className="border border-border p-3 text-green-600">✓</td>
                              <td className="border border-border p-3 text-green-600">✓</td>
                              <td className="border border-border p-3 text-yellow-600">With Approval</td>
                              <td className="border border-border p-3 text-yellow-600">With Approval</td>
                              <td className="border border-border p-3">Non-PHI</td>
                            </tr>
                            <tr>
                              <td className="border border-border p-3">Calendar Sync (External Apps)</td>
                              <td className="border border-border p-3 text-green-600">✓</td>
                              <td className="border border-border p-3 text-green-600">✓</td>
                              <td className="border border-border p-3 text-green-600">✓</td>
                              <td className="border border-border p-3 text-green-600">✓</td>
                              <td className="border border-border p-3">Non-PHI</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      
                      <div className="mt-4 text-sm text-muted-foreground space-y-1">
                        <p><strong>Footnotes:</strong></p>
                        <ul className="list-disc list-inside space-y-1">
                          <li>Explicit OA consent required before any access.</li>
                          <li>Sensitive household items (e.g., room scan photos) require OA/Admin approval for sharing.</li>
                          <li>Only non-PHI metadata (e.g., event time, title, location for general appointments) syncs unless you expressly opt in to share more details that might constitute PHI.</li>
                        </ul>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl font-bold text-foreground mb-4">Consent Workflows & Modals</h3>
                      <p className="text-muted-foreground mb-4">We use clear, step-by-step consent processes:</p>
                      
                      <div className="space-y-6">
                        <div>
                          <h4 className="text-lg font-semibold text-foreground">PHI Category Toggle:</h4>
                          <p className="text-muted-foreground"><strong>Location:</strong> Settings → Privacy → individual toggles with [Learn More] links (e.g., to a relevant FAQ or section explaining PHI sharing medications).</p>
                        </div>
                        
                        <div>
                          <h4 className="text-lg font-semibold text-foreground">Modal #1 ("Share PHI Data?"):</h4>
                          <ul className="list-disc list-inside text-muted-foreground space-y-1">
                            <li><strong>Title:</strong> "Share Your [Category] with [Role Name]?"</li>
                            <li><strong>Body:</strong> "You're about to share your [Category]—sensitive health information—with [Role Name]. This data is encrypted, and you can revoke access at any time in Settings."</li>
                            <li><strong>Buttons:</strong> [Cancel] [Confirm & Share]</li>
                            <li><strong>Learn More:</strong> Links back to this Addendum's Role-Based Permissions table.</li>
                          </ul>
                        </div>
                        
                        <div>
                          <h4 className="text-lg font-semibold text-foreground">Invite Member:</h4>
                          <ul className="list-disc list-inside text-muted-foreground space-y-1">
                            <li><strong>Trigger:</strong> "Invite Caregiver" or "Invite Family" in ThriveCircle.</li>
                            <li><strong>Modal #2 ("Inviting [Role Name]"):</strong>
                              <ul className="list-disc list-inside ml-4 space-y-1">
                                <li><strong>Title:</strong> "Invite [Role Name] to Your ThriveCircle"</li>
                                <li><strong>Body:</strong> "They'll view your shared categories and, for caregivers, may enter clinical updates. You control every permission in Settings."</li>
                                <li><strong>Buttons:</strong> [Back] [Send Invite]</li>
                                <li><strong>Learn More:</strong> Anchors to "Role-Based Permissions" above.</li>
                              </ul>
                            </li>
                          </ul>
                        </div>
                        
                        <div>
                          <h4 className="text-lg font-semibold text-foreground">Connect External Calendar:</h4>
                          <ul className="list-disc list-inside text-muted-foreground space-y-1">
                            <li><strong>Trigger:</strong> "Connect Calendar" for Admins or Family.</li>
                            <li><strong>Modal #3 ("Connect External Calendar?"):</strong>
                              <ul className="list-disc list-inside ml-4 space-y-1">
                                <li><strong>Title:</strong> "Sync ThriveCircle with Your Calendar"</li>
                                <li><strong>Body:</strong> "You'll sync appointment events (typically non-medical unless specified) to [Google/Apple Calendar]. No sensitive health data leaves SeniorThrive unless you explicitly opt in to share more."</li>
                                <li><strong>Buttons:</strong> [Cancel] [Allow Sync]</li>
                                <li><strong>Learn More:</strong> Points to the Integration section under "Non-PHI Calendar Sync."</li>
                              </ul>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl font-bold text-foreground mb-4">Audit & Revocation</h3>
                      <div className="space-y-4">
                        <div>
                          <h4 className="text-lg font-semibold text-foreground">Immediate Enforcement:</h4>
                          <p className="text-muted-foreground">Revoking consent via Settings → Privacy instantly hides all affected PHI from the relevant Circle member(s).</p>
                        </div>
                        
                        <div>
                          <h4 className="text-lg font-semibold text-foreground">Notifications:</h4>
                          <p className="text-muted-foreground">In-app toasts and optional email alerts to impacted members:</p>
                          <p className="text-muted-foreground italic">Example: "Access to [Focus OA Name]'s [Category] data was revoked by [Focus OA Name/Circle Admin Name] on May 20, 2025."</p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl font-bold text-foreground mb-4">Data Retention & Portability</h3>
                      <div className="space-y-4">
                        <div>
                          <h4 className="text-lg font-semibold text-foreground">Active PHI:</h4>
                          <p className="text-muted-foreground">Remains accessible within an active ThriveCircle according to the permissions you've set.</p>
                        </div>
                        
                        <div>
                          <h4 className="text-lg font-semibold text-foreground">Post-Circle/Account Closure:</h4>
                          <p className="text-muted-foreground">Upon ThriveCircle deletion or Focus OA account closure (including in the event of death), PHI is archived for a period of six months. This retention period supports data recovery needs and allows legally designated representatives to request data export. After this period, PHI is securely purged from our active systems, subject to any overriding legal or regulatory retention obligations.</p>
                        </div>
                        
                        <div>
                          <h4 className="text-lg font-semibold text-foreground">Export:</h4>
                          <p className="text-muted-foreground">You or your legally designated representative can request an export of your data.</p>
                        </div>
                      </div>
                    </div>

                    
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>;
};
export default Privacy;